import React, { useState, useEffect } from 'react'
import {
  InputGroup,
  H5,
  Spinner,
  Classes,
  Position,
  Tabs,
  Tab,
  Dialog,
  Intent,
} from '@blueprintjs/core'
import {
  IconButton,
  Confirmation,
  Flex,
  Button,
  Toaster,
  Avatar,
  Popover,
  DepartmentCatalog,
  HighlightedText,
  GridTableStatusEnum,
  NewGridTable,
  IGridTableColumn,
} from '@ui'
import api, { IEmployee, IDepartment } from '@api'
import { UseNeighborhoodHook } from './hooks/useNeighborhood'
import { pluralize } from '@utils'
import { useReload } from '@hooks/reloadContext'
import { useSelection } from '@hooks'
import useNeighborhoodEmployees from './hooks/useNeighborhoodEmployees'
import { Permissions, PermissionsType, IPermissionsOnNeighborhood } from '@permissions'
import track from '@track'
import Event from '@track/Event'
import Catalog, { CatalogProps, ItemProps } from '@ui/GenericCatalog'
import classnames from 'classnames'
import css from './employeesManageSection.module.scss'

const placeholder = <span className={Classes.SKELETON}>placeholder</span>

export default function EmployeesManageSection({
  neighborhood,
}: {
  neighborhood: UseNeighborhoodHook
}) {
  const selection = useSelection()
  const reload = useReload()
  const [disableConfirmation, setDisableConfirmation] = useState(false)

  const [showSearch, setShowSearch] = useState(false)
  const [withUndo, setWithUndo] = useState<IEmployee[]>([])

  const employees = useNeighborhoodEmployees(neighborhood.id)
  const [seated, setSeated] = useState<number | null>(null)
  const [dept, setDept] = useState<IDepartment | null>(null)

  useEffect(() => {
    setWithUndo(employees.list)
  }, [employees.list])

  const helperText = selection.count
    ? `${selection.count} selected`
    : employees.query
    ? `${employees.count} filtered ${pluralize(employees.count, 'record')}`
    : `${employees.count} ${pluralize(employees.count, 'record')}`

  const columnsEmployees: IGridTableColumn<IEmployee>[] = [
    {
      label: 'Employee',
      Cell: ({ cell, skeleton }) =>
        cell ? (
          <Flex align="center">
            <Avatar name={cell.data.name} assets={cell.data.assets} size={Avatar.Size.XS} />
            <div style={{ marginLeft: 10 }}>
              {cell.data.name}
              <div style={{ fontSize: 12, color: 'var(--grey1)' }}>{cell.data.email}</div>
            </div>
          </Flex>
        ) : (
          <div
            className={classnames({
              [Classes.SKELETON]: skeleton,
            })}
          >
            placeholder
          </div>
        ),
    },
    {
      label: 'Department / Team',
      Cell: ({ cell, skeleton }) => {
        return cell ? (
          <div>
            {`${cell.data.dept ? cell.data.dept.name : ''} ${
              cell.data.team ? ' | ' + cell.data.team.name : ''
            }`}
          </div>
        ) : (
          <div
            className={classnames({
              [Classes.SKELETON]: skeleton,
            })}
          >
            place
          </div>
        )
      },
    },
    {
      label: '',

      Cell: ({ cell, skeleton, isHighlighted }) => {
        if (!cell || !isHighlighted) return null

        const { data: employee } = cell

        return (
          <Permissions
            type={PermissionsType.NEIGHBORHOOD}
            on={neighborhood.item ? [neighborhood.item] : null}
          >
            {(permissions) => {
              const { canRemoveEmployees } = permissions as IPermissionsOnNeighborhood

              return canRemoveEmployees ? (
                <div className="w-full" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton
                    round
                    minimal
                    tooltip={`Remove ${employee.name}`}
                    className="remove-icon"
                    iconName="times"
                    onClick={() => {
                      const removedEmployee = employee

                      setWithUndo((prev) => [...prev.filter((i) => employee.id !== i.id)])
                      selection.clear()

                      api.neighborhood
                        .update({
                          id: neighborhood.id,
                          removeEmployees: [{ id: employee.id }],
                        })
                        .then(() => {
                          Toaster.success({
                            action: {
                              onClick: () => {
                                api.neighborhood
                                  .update({
                                    id: neighborhood.id,
                                    employees: [{ id: employee.id }],
                                  })
                                  .then(() => reload.neighborhoods())
                                  .catch((error) => Toaster.fail({ error }))
                                setWithUndo((prev) => [removedEmployee, ...prev])

                                track.neighborhoodDialog({
                                  eventName: Event.UNDO_CLICK,
                                  props: {
                                    component: 'Employee Manage',
                                    actionName: 'remove',
                                  },
                                })
                              },
                              text: 'Undo',
                            },
                            message: 'Employee removed from Neighborhood',
                          })
                        })
                        .then(() => reload.neighborhoods())
                        .catch((error) => {
                          Toaster.dismissAll()
                          Toaster.fail({ error })
                          reload.neighborhoods()
                        })

                      track.neighborhoodDialog({
                        eventName: Event.RECORD_REMOVE,
                        props: {
                          component: 'Employee Manage',
                        },
                      })
                    }}
                  />
                </div>
              ) : null
            }}
          </Permissions>
        )
      },
    },
  ]

  function addDepartment(dept: IDepartment) {
    api.neighborhood
      .update({
        id: neighborhood.id,
        departments: [{ id: dept.id }],
      })
      .then(() =>
        Toaster.success({
          message: 'Employees from department added to Neighborhood',
        })
      )
      .then(() => reload.neighborhoods())
      .catch((error) => {
        Toaster.dismissAll()
        Toaster.fail({ error })
        reload.neighborhoods()
      })

    track.neighborhoodDialog({
      eventName: Event.RECORD_ADD,
      props: {
        component: 'Employee Manage',
      },
    })
  }

  const formatItems = (items: IEmployee[]) => {
    return items.map((i) => ({
      data: i,
    }))
  }

  const formatedItems = formatItems(withUndo)

  return (
    <Permissions
      type={PermissionsType.NEIGHBORHOOD}
      on={neighborhood.item ? [neighborhood.item] : null}
    >
      {(permissions) => {
        const { canAddEmployees, canRemoveEmployees } = permissions as IPermissionsOnNeighborhood

        return (
          <>
            <Flex
              align="center"
              justify="space-between"
              style={{
                marginTop: 40,
                height: 40,
              }}
            >
              <Flex align="center">
                <H5
                  style={{
                    marginBottom: 0,
                    marginRight: 6,
                    lineHeight: '30px',
                  }}
                >
                  Employees
                </H5>
                {showSearch ? (
                  <InputGroup
                    autoFocus
                    leftIcon="search"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      employees.run(e.target.value)
                    }}
                    placeholder="Search employees..."
                    rightElement={
                      <RightElement
                        showWithoutQuery
                        query={employees.query}
                        onClear={() => {
                          if (employees.query) employees.run('')
                          setShowSearch(false)
                        }}
                        isQueryResultLoading={employees.isLoading}
                      />
                    }
                    value={employees.query}
                  />
                ) : (
                  <IconButton round minimal iconName="search" onClick={() => setShowSearch(true)} />
                )}
              </Flex>
            </Flex>
            <Flex justify="space-between">
              <Flex align="center">
                {Boolean(selection.count) && (
                  <span style={{ paddingRight: 10 }}>
                    <Confirmation
                      shouldHide={disableConfirmation}
                      enabled={!disableConfirmation}
                      text={`Remove ${pluralize(selection.count, 'employee')} from neighborhood?`}
                      onConfirm={() => {
                        const employeesForDelete = withUndo.filter((i) =>
                          Object.keys(selection.items).includes(i.id)
                        )

                        const employeeIds = Object.entries(selection.items)
                          .filter(([_, value]) => Boolean(value))
                          .map(([id]) => id)

                        setWithUndo((prev) => [...prev.filter((i) => !employeeIds.includes(i.id))])
                        setDisableConfirmation(true)
                        selection.clear()

                        api.neighborhood
                          .update({
                            id: neighborhood.id,
                            removeEmployees: employeeIds.map((employee) => ({
                              id: employee,
                            })),
                          })
                          .then(() => {
                            setDisableConfirmation(false)

                            setWithUndo((prev) => [
                              ...prev.filter((i) => !employeeIds.includes(i.id)),
                            ])
                          })
                          .then(() => {
                            selection.clear()
                            Toaster.success({
                              action: {
                                onClick: () => {
                                  setWithUndo((prev) => [...employeesForDelete, ...prev])
                                  setDisableConfirmation(false)

                                  api.neighborhood
                                    .update({
                                      id: neighborhood.id,
                                      employees: employeesForDelete.map(({ id }) => ({
                                        id,
                                      })),
                                    })
                                    .then(() => reload.neighborhoods())
                                    .catch((error) => Toaster.fail({ error }))

                                  track.neighborhoodDialog({
                                    eventName: Event.UNDO_CLICK,
                                    props: {
                                      component: 'Employee Manage',
                                      actionName: 'remove',
                                    },
                                  })
                                },
                                text: 'Undo',
                              },
                              message: `${pluralize(
                                selection.count,
                                'Employee'
                              )} removed from Neighborhood`,
                            })
                          })
                          .then(() => reload.neighborhoods())
                          .catch((error) => {
                            setDisableConfirmation(false)
                            Toaster.dismissAll()
                            Toaster.fail({ error })
                            reload.neighborhoods()
                          })

                        track.neighborhoodDialog({
                          eventName: Event.BATCH_ACTION,
                          props: {
                            actionName: 'Delete employees',
                            actionValue: null,
                            batchCount: employeeIds.length,
                          },
                        })
                      }}
                    >
                      <Button text="Remove" />
                    </Confirmation>
                  </span>
                )}
                <Flex align="baseline">
                  {!employees.isLoading && (
                    <span style={{ color: 'var(--grey1)' }}>{helperText}</span>
                  )}
                  {employees.isLoading && placeholder}
                  {Boolean(selection.count) && (
                    <IconButton
                      minimal
                      tooltip="Clear selection"
                      iconName="times"
                      style={{ marginLeft: 4 }}
                      data-button="clear_selection"
                      onClick={() => {
                        selection.clear()

                        track.neighborhoodDialog({
                          eventName: Event.CLEAR_SELECTION,
                          props: {
                            component: 'Employee Manage',
                          },
                        })
                      }}
                    />
                  )}
                </Flex>
              </Flex>
              {canAddEmployees ? (
                <EmployeeAndDepartmentSelect
                  onSelectDepartment={async (dept) => {
                    if (dept) {
                      try {
                        const spacesAssigned = await api.department.seatedCount({
                          id: dept.id,
                        })

                        if (!spacesAssigned) {
                          addDepartment(dept)
                        } else {
                          setSeated(spacesAssigned)
                          setDept(dept)
                        }
                      } catch (error: any) {
                        Toaster.fail({ error })
                      }
                    }
                  }}
                  onSelectEmployee={(empl) => {
                    if (empl) {
                      api.neighborhood
                        .update({
                          id: neighborhood.id,
                          employees: [{ id: empl.id }],
                        })
                        .then(() =>
                          Toaster.success({
                            message: 'Employee added to Neighborhood',
                          })
                        )
                        .then(() => reload.neighborhoods())
                        .catch((error) => {
                          Toaster.dismissAll()
                          Toaster.fail({ error })
                          reload.neighborhoods()
                        })

                      track.neighborhoodDialog({
                        eventName: Event.RECORD_ADD,
                        props: {
                          component: 'Employee Manage',
                        },
                      })
                    }
                  }}
                />
              ) : null}
            </Flex>
            <div className={css.table_wrapper}>
              <NewGridTable<IEmployee>
                status={
                  employees.isLoading ? GridTableStatusEnum.LOADING : GridTableStatusEnum.IDLE
                }
                customColumnWidths={['auto', 'auto', '50px']}
                sticky={false}
                noDataMessage={employees.query ? undefined : 'No Employees associated yet'}
                columns={columnsEmployees}
                selection={canRemoveEmployees ? selection : undefined}
                data={formatedItems}
                skeletonSize={3}
              />
            </div>
            {employees.list && employees.pageInfo.hasNextPage && (
              <Button
                minimal
                style={{ marginTop: 5, width: 100 }}
                text={`View ${
                  employees.count - employees.list.length > 5
                    ? '5'
                    : employees.count - employees.list.length
                } more`}
                onClick={() => {
                  employees.loadMore()

                  track.neighborhoodDialog({
                    eventName: Event.PAGE,
                    props: {
                      component: 'Employee Manage',
                    },
                  })
                }}
              />
            )}
            {seated && (
              <ConfirmationDialog
                onConfirm={() => {
                  if (dept) {
                    addDepartment(dept)
                    setDept(null)
                  }
                }}
                seatedCount={seated}
                show={Boolean(dept)}
                onClose={() => setDept(null)}
              />
            )}
          </>
        )
      }}
    </Permissions>
  )
}

function ConfirmationDialog({
  show,
  seatedCount,
  onConfirm,
  onClose,
}: {
  show: boolean
  seatedCount: number
  onConfirm: () => void
  onClose: () => void
}) {
  return (
    <Dialog isOpen={show} title="Warning" onClose={onClose}>
      <div className={Classes.DIALOG_BODY}>
        <div style={{ marginTop: 5, marginBottom: 20 }}>
          {`You are about to unseat ${seatedCount} ${pluralize(
            seatedCount,
            'employee'
          )} and move them to this neighborhood.`}
        </div>
        <Flex>
          <Flex style={{ marginLeft: 'auto' }}>
            <div style={{ marginRight: 8 }}>
              <Button type="button" onClick={onClose} text="Cancel" />
            </div>
            <Button intent={Intent.PRIMARY} type="submit" onClick={onConfirm} text="Assign" />
          </Flex>
        </Flex>
      </div>
    </Dialog>
  )
}

const initTabId = '1'

function EmployeeAndDepartmentSelect({
  onSelectEmployee,
  onSelectDepartment,
}: {
  onSelectEmployee: (item: IEmployee | null) => void
  onSelectDepartment: (item: IDepartment | null) => void
}) {
  const [tabId, setTabId] = useState(initTabId)

  return (
    <Popover
      minimal
      position={Position.BOTTOM}
      content={
        <div className="bp4-tabs-override">
          <Tabs
            selectedTabId={tabId}
            onChange={(newTabId: string) => setTabId(newTabId)}
            renderActiveTabPanelOnly
          >
            <Tab
              id="1"
              title="Employee"
              panel={
                <div style={{ height: 300 }}>
                  <EmployeeCatalog onSelect={onSelectEmployee} />
                </div>
              }
            />
            <Tab
              id="2"
              title="Department"
              panel={
                <div style={{ height: 300 }}>
                  <DepartmentCatalog onSelect={onSelectDepartment} />
                </div>
              }
            />
          </Tabs>
        </div>
      }
    >
      <Button iconName="plus" text="Employee/Department" />
    </Popover>
  )
}

function RightElement({
  showWithoutQuery,
  query,
  onClear,
  isQueryResultLoading,
}: {
  query: string
  onClear: () => void
  isQueryResultLoading: boolean
  showWithoutQuery?: boolean
}) {
  if (isQueryResultLoading && query) return <Spinner size={18} />
  if ((showWithoutQuery !== undefined && showWithoutQuery) || query)
    return (
      <IconButton
        minimal
        iconName="times"
        onClick={() => {
          onClear?.()

          track.neighborhoodDialog({
            eventName: Event.CLEAR_FILTER,
            props: {
              component: 'Employee Manage',
              filterName: 'Query',
              filterValue: null,
            },
          })
        }}
      />
    )
  return null
}

function EmployeeCatalog(props: CatalogProps<IEmployee>) {
  return <Catalog<IEmployee> list={api.employee.search} Item={EmployeeItem} {...props} />
}

function EmployeeItem({ skeleton, data, refItem, query }: ItemProps<IEmployee>) {
  if (skeleton) {
    return (
      <Flex align="center" style={{ minWidth: 250, padding: '3px 0' }}>
        <Avatar skeleton size={Avatar.Size.XS} name="" assets={[]} />
        <div style={{ marginLeft: 10 }}>
          <span className={Classes.SKELETON}>First Last name</span>
          <div
            style={{
              fontSize: 12,
              lineHeight: '14px',
              color: 'var(--grey1)',
            }}
          >
            <span className={Classes.SKELETON}>first.last@companyname.com</span>
          </div>
        </div>
      </Flex>
    )
  }

  return (
    <div
      ref={refItem as React.Ref<HTMLDivElement>}
      className={classnames('flex items-center text-sm truncate')}
    >
      <Avatar
        size={Avatar.Size.XS}
        name={(data as IEmployee).name}
        assets={(data as IEmployee).assets}
      />
      <div className="ml-2 truncate">
        <span className="font-medium">
          <HighlightedText query={query} text={(data as IEmployee).name} />
        </span>
        <div className="font-sm text-grey-600 truncate">
          <HighlightedText query={query} text={(data as IEmployee).email} />
        </div>
      </div>
    </div>
  )
}
